// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Data tab's pins on the immersive globe (the AR refresh,
 * 2026-10-04; docs/AYNI_TOOLS_DATA_PLAN.md phases 4 and 5): each pin a
 * small sphere in its colour on the surface, and a label with the value it
 * reads at the end of a short stem.
 *
 * The head is a child of the globe, so it turns and scales with it and the
 * globe hides it when it is round the back. The label and its stem are
 * placed again every frame (`update`), in the scene rather than on the
 * globe: they lean toward the viewer, up and to the right for a pin in the
 * northern hemisphere and down and to the left for one in the southern, so
 * they never stand straight out over the data around the pin (owner,
 * 2026-10-04), and they hide when the pin turns away.
 *
 * The pins themselves live in main.ts's PinStore, the same store the
 * browser's Data tab and the chatbot use: a pin dropped in the headset
 * is in the browser's list when the session ends, and the other way
 * round.
 */

import type * as THREE from 'three'
import { latLonToGlobeLocal } from './vrGeoMedia'
import { focusZoomVersion, getFocusZoom, isZoomed, zoomedGlobeLocal } from './vrFocusZoom'

export interface VrPinMark {
  id: string
  /** CSS colour (the pin's colour in the Data tab). */
  color: string
  lat: number
  lon: number
  /** The value it reads, as the Data tab prints it; empty while unread. */
  label: string
  /**
   * AYNI (owner, 2026-10-18): the track a pin following the highest or
   * lowest value left (`measure/followExtremes.ts`), as segments; absent
   * or empty: none.
   */
  track?: ReadonlyArray<ReadonlyArray<{ lat: number; lon: number }>>
}

export interface VrPinsHandle {
  /** Show these pins (null or empty: none). Applies only what changed. */
  set(pins: readonly VrPinMark[] | null): void
  /** Lean the labels toward the viewer; call every frame, after the globe has moved. */
  update(camera: THREE.Camera): void
  dispose(): void
}

/** Head size, in globe-local units (the globe's radius is `globeRadius`); 25 % under the first version's 0.036. */
export const HEAD_RADIUS_FACTOR = 0.027
/** The label's size and the stem's length in metres at scale 1; they follow the globe's size within limits. */
const LABEL_W = 0.13
const LABEL_H = 0.049
const STEM_M = 0.085
const LABEL_CANVAS_W = 384
const LABEL_CANVAS_H = 144
/** A pin facing the viewer less than this (cosine of the angle) has its label hidden. */
const MIN_FACING = 0.12

/**
 * The direction a label leans from its pin, in world space: toward the
 * viewer, and up-right for the northern hemisphere, down-left for the
 * southern. Pure (plain vectors), so the rule is tested without a scene.
 */
export function labelDirection(
  toViewer: { x: number; y: number; z: number },
  right: { x: number; y: number; z: number },
  up: { x: number; y: number; z: number },
  northern: boolean,
): { x: number; y: number; z: number } {
  const s = northern ? 1 : -1
  const x = toViewer.x * 0.55 + (right.x + up.x) * 0.6 * s
  const y = toViewer.y * 0.55 + (right.y + up.y) * 0.6 * s
  const z = toViewer.z * 0.55 + (right.z + up.z) * 0.6 * s
  const len = Math.hypot(x, y, z) || 1
  return { x: x / len, y: y / len, z: z / len }
}

interface Drawn {
  key: string
  pin: VrPinMark
  head: THREE.Mesh
  headMaterial: THREE.MeshBasicMaterial
  stem: THREE.Line
  stemGeometry: THREE.BufferGeometry
  stemMaterial: THREE.LineBasicMaterial
  sprite: THREE.Sprite
  spriteMaterial: THREE.SpriteMaterial
  texture: THREE.CanvasTexture
  canvas: HTMLCanvasElement
  trackKey: string
  track: THREE.LineSegments | null
  /** AYNI (2026-10-22): the headset zoom the head was placed for (vrFocusZoom.ts). */
  zoomVersion: number
}

/** The longest piece a track is drawn in, degrees of arc: shorter than this, a chord stays clear of the surface. */
const TRACK_STEP_DEG = 2
/** Just above the data, under the pin's head. */
const TRACK_LIFT = 1.003

/**
 * A track's segments as line pieces on the globe (pairs of points, in
 * globe-local space), each step cut along the great circle so no chord
 * dips under the surface. Pure, for the tests.
 */
export function trackPositions(
  segments: ReadonlyArray<ReadonlyArray<{ lat: number; lon: number }>>,
  radius: number,
): number[] {
  const out: number[] = []
  for (const seg of segments) {
    for (let i = 1; i < seg.length; i++) {
      const a = latLonToGlobeLocal(seg[i - 1]!.lat, seg[i - 1]!.lon, 1)
      const b = latLonToGlobeLocal(seg[i]!.lat, seg[i]!.lon, 1)
      const dot = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))
      const steps = Math.max(1, Math.ceil((Math.acos(dot) * 180) / Math.PI / TRACK_STEP_DEG - 1e-9))
      let prev = a
      for (let k = 1; k <= steps; k++) {
        const f = k / steps
        const x = a.x + (b.x - a.x) * f
        const y = a.y + (b.y - a.y) * f
        const z = a.z + (b.z - a.z) * f
        const len = Math.hypot(x, y, z) || 1
        const next = { x: x / len, y: y / len, z: z / len }
        out.push(prev.x * radius, prev.y * radius, prev.z * radius, next.x * radius, next.y * radius, next.z * radius)
        prev = next
      }
    }
  }
  return out
}

function paintLabel(canvas: HTMLCanvasElement, color: string, text: string): void {
  const g = canvas.getContext('2d')
  if (!g) return
  const w = canvas.width
  const h = canvas.height
  g.clearRect(0, 0, w, h)
  g.font = '700 64px system-ui, -apple-system, sans-serif'
  const textW = Math.min(w - 40, g.measureText(text || '·').width)
  const boxW = textW + 56
  const x = (w - boxW) / 2
  g.beginPath()
  if (typeof g.roundRect === 'function') g.roundRect(x, 16, boxW, h - 32, 24)
  else g.rect(x, 16, boxW, h - 32)
  g.fillStyle = 'rgba(13, 13, 18, 0.88)'
  g.fill()
  g.lineWidth = 6
  g.strokeStyle = color
  g.stroke()
  g.fillStyle = '#e8eaf0'
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.fillText(text || '·', w / 2, h / 2 + 2, w - 40)
}

export function createVrPins(
  THREE_: typeof THREE,
  globe: THREE.Mesh,
  globeRadius: number,
  scene: THREE.Object3D,
): VrPinsHandle {
  const drawn = new Map<string, Drawn>()
  const headGeometry = new THREE_.SphereGeometry(globeRadius * HEAD_RADIUS_FACTOR, 16, 12)
  const headWorld = new THREE_.Vector3()
  const centre = new THREE_.Vector3()
  const normal = new THREE_.Vector3()
  const camPos = new THREE_.Vector3()
  const toViewer = new THREE_.Vector3()
  const right = new THREE_.Vector3()
  const up = new THREE_.Vector3()
  const end = new THREE_.Vector3()

  function build(pin: VrPinMark): Drawn {
    const headMaterial = new THREE_.MeshBasicMaterial({ color: new THREE_.Color(pin.color) })
    const head = new THREE_.Mesh(headGeometry, headMaterial)
    head.renderOrder = 11
    globe.add(head)
    const stemGeometry = new THREE_.BufferGeometry()
    stemGeometry.setAttribute('position', new THREE_.Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3))
    const stemMaterial = new THREE_.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthTest: false })
    const stem = new THREE_.Line(stemGeometry, stemMaterial)
    stem.renderOrder = 12
    stem.frustumCulled = false
    scene.add(stem)
    const canvas = document.createElement('canvas')
    canvas.width = LABEL_CANVAS_W
    canvas.height = LABEL_CANVAS_H
    const texture = new THREE_.CanvasTexture(canvas)
    texture.colorSpace = THREE_.SRGBColorSpace
    const spriteMaterial = new THREE_.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false })
    const sprite = new THREE_.Sprite(spriteMaterial)
    sprite.renderOrder = 13
    scene.add(sprite)
    return { key: '', pin, head, headMaterial, stem, stemGeometry, stemMaterial, sprite, spriteMaterial, texture, canvas, trackKey: '', track: null, zoomVersion: -1 }
  }

  function removeTrack(d: Drawn): void {
    if (!d.track) return
    globe.remove(d.track)
    d.track.geometry.dispose()
    ;(d.track.material as THREE.Material).dispose()
    d.track = null
  }

  /** AYNI (owner, 2026-10-18): the pin's track, a child of the globe so the globe hides it round the back. */
  function applyTrack(d: Drawn, pin: VrPinMark): void {
    const segments = pin.track ?? []
    const key = `${pin.color}|${segments.map((s) => `${s.length}:${s[s.length - 1]?.lat}:${s[s.length - 1]?.lon}`).join(';')}`
    if (key === d.trackKey) return
    d.trackKey = key
    removeTrack(d)
    const positions = trackPositions(segments, globeRadius * TRACK_LIFT)
    if (!positions.length) return
    const geometry = new THREE_.BufferGeometry()
    geometry.setAttribute('position', new THREE_.Float32BufferAttribute(positions, 3))
    const material = new THREE_.LineBasicMaterial({ color: new THREE_.Color(pin.color), transparent: true, opacity: 0.95 })
    const line = new THREE_.LineSegments(geometry, material)
    line.renderOrder = 10
    globe.add(line)
    d.track = line
  }

  function apply(d: Drawn, pin: VrPinMark): void {
    d.pin = pin
    applyTrack(d, pin)
    const key = `${pin.lat}|${pin.lon}|${pin.color}|${pin.label}`
    if (key === d.key) return
    d.key = key
    placeHead(d)
    d.headMaterial.color.set(pin.color)
    paintLabel(d.canvas, pin.color, pin.label)
    d.texture.needsUpdate = true
  }

  /** The head where the headset's zoom draws its place (vrFocusZoom.ts). */
  function placeHead(d: Drawn): void {
    const surface = zoomedGlobeLocal(d.pin.lat, d.pin.lon, globeRadius * 1.004)
    d.head.position.set(surface.x, surface.y, surface.z)
    d.zoomVersion = focusZoomVersion()
  }

  function remove(d: Drawn): void {
    removeTrack(d)
    globe.remove(d.head)
    scene.remove(d.stem)
    scene.remove(d.sprite)
    d.headMaterial.dispose()
    d.stemGeometry.dispose()
    d.stemMaterial.dispose()
    d.texture.dispose()
    d.spriteMaterial.dispose()
  }

  return {
    set(pins) {
      const list = pins ?? []
      const keep = new Set(list.map((p) => p.id))
      for (const [id, d] of drawn) {
        if (!keep.has(id)) {
          remove(d)
          drawn.delete(id)
        }
      }
      for (const pin of list) {
        let d = drawn.get(pin.id)
        if (!d) {
          d = build(pin)
          drawn.set(pin.id, d)
        }
        apply(d, pin)
      }
    },
    update(camera) {
      if (drawn.size === 0) return
      globe.updateMatrixWorld()
      globe.getWorldPosition(centre)
      camera.getWorldPosition(camPos)
      const e = camera.matrixWorld.elements
      right.set(e[0]!, e[1]!, e[2]!).normalize()
      up.set(e[4]!, e[5]!, e[6]!).normalize()
      const size = Math.max(0.7, Math.min(1.6, globe.scale.x))
      // AYNI (2026-10-22): heads follow the zoom; tracks (drawn as chords) hide while it is on.
      const zoomVersion = focusZoomVersion()
      const zoomed = isZoomed(getFocusZoom())
      for (const d of drawn.values()) {
        if (d.zoomVersion !== zoomVersion) placeHead(d)
        if (d.track) d.track.visible = !zoomed
        d.head.getWorldPosition(headWorld)
        normal.copy(headWorld).sub(centre).normalize()
        toViewer.copy(camPos).sub(headWorld).normalize()
        const facing = normal.dot(toViewer)
        const show = facing > MIN_FACING
        d.sprite.visible = show
        d.stem.visible = show
        if (!show) continue
        const northern = d.pin.lat >= 0
        const dir = labelDirection(toViewer, right, up, northern)
        end.set(headWorld.x + dir.x * STEM_M * size, headWorld.y + dir.y * STEM_M * size, headWorld.z + dir.z * STEM_M * size)
        const pos = d.stemGeometry.getAttribute('position') as THREE.BufferAttribute
        pos.setXYZ(0, headWorld.x, headWorld.y, headWorld.z)
        pos.setXYZ(1, end.x, end.y, end.z)
        pos.needsUpdate = true
        d.sprite.position.copy(end)
        // The label's near corner sits on the stem's end: its bottom-left
        // going up-right, its top-right going down-left.
        d.sprite.center.set(northern ? 0 : 1, northern ? 0 : 1)
        d.sprite.scale.set(LABEL_W * size, LABEL_H * size, 1)
      }
    },
    dispose() {
      for (const d of drawn.values()) remove(d)
      drawn.clear()
      headGeometry.dispose()
    },
  }
}
